import { X } from "lucide-react"

import { TIPOS, type TipoTarea } from "./TarjetaTarea"

/**
 * La vista del tablero, guardada en la URL.
 *
 * ⚠ **En la URL y no en el estado del componente**, y no es un detalle: «mándame las
 * urgentes sin dueño» es una frase que alguien dice todos los días, y sin esto la
 * única forma de contarla es por escrito. Además sobrevive al refresco, al botón de
 * atrás y a abrir el tablero en otra pestaña para comparar dos vistas.
 *
 * ⚠ **Sólo se escribe lo que no es el valor por defecto.** Una URL con seis
 * parámetros que dicen «todo» no se puede leer de un vistazo ni pegar en un mensaje;
 * `/tareas` significa el tablero entero y `/tareas?sin=1` significa una cosa.
 */

/**
 * Cómo se mira el módulo de tareas.
 *
 * **Resumen** es la puerta: qué me toca, con el mes al lado. **Tablero** mueve
 * cinco tarjetas y **Lista** repasa cincuenta —son la misma información con dos
 * densidades, y por eso van juntas—. **Carga** cuenta por persona.
 *
 * ⚠ **El calendario dejó de ser una pestaña.** A pantalla completa ocupaba todo
 * para decir lo que dice —qué días están cargados—, y obligaba a cambiar de pestaña
 * para cruzarlo con los contadores, que es justo lo que uno quiere hacer con él.
 * Vive dentro de «Resumen», en compacto. Una URL vieja con `?vista=calendario` cae
 * al resumen, que es donde está ahora.
 *
 * ⚠ **«Carga» pide `tarea:asignar`.** Es la vista por responsable que se retiró en
 * su día: agrupar por persona sólo dice algo cuando se ven las tareas de todos, y
 * este permiso —el que ya marca «coordina»— es el que garantiza que así sea.
 *
 * ⚠ **«carga» sigue en esta lista aunque no todos la vean**, porque esto es lo que
 * valida la URL. Quién puede abrirla lo decide la pantalla, con el permiso delante;
 * y el servidor, que responde 403 pase lo que pase aquí.
 */
export type Vista = "resumen" | "estado" | "lista" | "carga"

export const VISTAS: { valor: Vista; etiqueta: string }[] = [
  { valor: "resumen", etiqueta: "Resumen" },
  { valor: "estado", etiqueta: "Tablero" },
  { valor: "lista", etiqueta: "Lista" },
  { valor: "carga", etiqueta: "Carga" },
]

export type Filtros = {
  buscar: string
  /**
   * De quién son las tareas que se miran. `null` = de todos.
   *
   * ⚠ **Distinto de `soloMias`**, que es un interruptor sobre uno mismo y no
   * necesita saber de nadie. Éste es «enséñame las de Ana», y existe porque la vista
   * de carga cuenta por persona: un contador que no abre lo que cuenta obliga a
   * repetir el filtro a mano en la otra pestaña, y a los dos días no se mira.
   */
  asignadoA: number | null
  soloMias: boolean
  sinAsignar: boolean
  soloAbiertas: boolean
  tipo: TipoTarea | ""
  vista: Vista
}

export const FILTROS_VACIOS: Filtros = {
  buscar: "",
  asignadoA: null,
  soloMias: false,
  sinAsignar: false,
  soloAbiertas: false,
  tipo: "",
  vista: "resumen",
}

export function leerFiltros(params: URLSearchParams): Filtros {
  const tipo = params.get("tipo") ?? ""
  // Un `de` que no es un número —tecleado a mano, o recortado al pegar— se ignora en
  // vez de mandarse al servidor, que lo rechazaría con un 422 sobre un filtro que
  // quien mira no recuerda haber puesto.
  const de = Number(params.get("de"))
  return {
    buscar: params.get("q") ?? "",
    asignadoA: Number.isSafeInteger(de) && de > 0 ? de : null,
    soloMias: params.get("mias") === "1",
    sinAsignar: params.get("sin") === "1",
    soloAbiertas: params.get("abiertas") === "1",
    // Un tipo desconocido en la URL —tecleado a mano, o de una versión anterior— se
    // ignora en vez de mandarse al servidor, que lo rechazaría con un 422 sobre un
    // filtro que el usuario no recuerda haber puesto.
    tipo: TIPOS.some((t) => t.valor === tipo) ? (tipo as TipoTarea) : "",
    // Un valor desconocido cae al resumen en vez de dejar la pantalla en blanco: la
    // URL la puede haber escrito una versión anterior —`?vista=calendario` lo fue
    // hasta que el mes se mudó dentro del resumen— o una mano.
    vista: VISTAS.some((v) => v.valor === params.get("vista"))
      ? (params.get("vista") as Vista)
      : "resumen",
  }
}

export function escribirFiltros(f: Filtros): URLSearchParams {
  const params = new URLSearchParams()
  if (f.buscar.trim() !== "") params.set("q", f.buscar.trim())
  if (f.asignadoA !== null) params.set("de", String(f.asignadoA))
  if (f.soloMias) params.set("mias", "1")
  if (f.sinAsignar) params.set("sin", "1")
  if (f.soloAbiertas) params.set("abiertas", "1")
  if (f.tipo !== "") params.set("tipo", f.tipo)
  if (f.vista !== "resumen") params.set("vista", f.vista)
  return params
}

/** Los parámetros que entiende `GET /tareas`. Distintos de los de la URL a propósito. */
export function consultaDeApi(f: Filtros, usuarioId: number | undefined): URLSearchParams {
  const params = new URLSearchParams({ limit: "100" })
  if (f.buscar.trim() !== "") params.set("buscar", f.buscar.trim())
  // El explícito manda sobre «sólo las mías»: si están los dos, es que se acaba de
  // pulsar un nombre, y el servidor sólo entiende un `asignado_a`.
  if (f.asignadoA !== null) params.set("asignado_a", String(f.asignadoA))
  else if (f.soloMias && usuarioId !== undefined) params.set("asignado_a", String(usuarioId))
  if (f.sinAsignar) params.set("sin_asignar", "true")
  if (f.soloAbiertas) params.set("abiertas", "true")
  if (f.tipo !== "") params.set("tipo", f.tipo)
  return params
}

type Props = {
  filtros: Filtros
  onCambiar: (f: Filtros) => void
  /** Para poner nombre al filtro por responsable. Sin él sólo se vería el número. */
  nombreDe?: (id: number | null) => string | undefined
}

export function FiltrosTareas({ filtros, onCambiar, nombreDe }: Props) {
  const cambiar = (parcial: Partial<Filtros>) => onCambiar({ ...filtros, ...parcial })
  const borde = { borderColor: "var(--border)" }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-4">
      <label className="text-sm" htmlFor="buscar-tarea">
        Buscar
        <input
          id="buscar-tarea"
          className="ml-2 h-9 rounded-[var(--radius-control)] border px-2 text-sm"
          style={borde}
          value={filtros.buscar}
          onChange={(e) => cambiar({ buscar: e.target.value })}
          placeholder="Título o código"
        />
      </label>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={filtros.soloMias}
          onChange={(e) => cambiar({ soloMias: e.target.checked })}
        />
        Sólo las mías
      </label>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={filtros.sinAsignar}
          onChange={(e) => cambiar({ sinAsignar: e.target.checked })}
        />
        Sin dueño
      </label>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={filtros.soloAbiertas}
          onChange={(e) => cambiar({ soloAbiertas: e.target.checked })}
        />
        Sólo abiertas
      </label>

      {/* ⚠ Se pinta como quitable y no como un filtro invisible: se llega aquí desde
          la vista de carga, así que la lista sale ya recortada sin que nadie haya
          tocado esta barra, y sin la ficha se lee como que esa persona es la única
          que tiene tareas. */}
      {filtros.asignadoA !== null && (
        <span
          className="flex items-center gap-1 rounded-full border px-2 py-1 text-sm"
          style={{ borderColor: "var(--border)" }}
        >
          {nombreDe?.(filtros.asignadoA) ?? `Usuario ${filtros.asignadoA}`}
          <button
            type="button"
            onClick={() => cambiar({ asignadoA: null })}
            aria-label="Quitar el filtro por responsable"
          >
            <X size={14} aria-hidden={true} />
          </button>
        </span>
      )}

      <label className="flex items-center gap-2 text-sm" htmlFor="filtro-tipo">
        Tipo
        <select
          id="filtro-tipo"
          value={filtros.tipo}
          onChange={(e) => cambiar({ tipo: e.target.value as TipoTarea | "" })}
          className="h-9 rounded-[var(--radius-control)] border px-2 text-sm"
          style={borde}
        >
          <option value="">Todos</option>
          {TIPOS.map((t) => (
            <option key={t.valor} value={t.valor}>
              {t.etiqueta}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}
